import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LayoutTemplate, Plus, Pencil, Trash2, Check, Shield, Mail, KeySquare, MessagesSquare, Inbox, AppWindow } from "lucide-react";
import { byLastName } from "@/lib/format";

const EMPTY = { name: "", description: "", department: "", companyName: "", defaultJobTitle: "", defaultManager: "", defaultManagerName: "", usageLocation: "US", groups: [], distributionLists: [], licenses: [], teams: [], applications: [], sharedMailboxes: [] };

function Chips({ label, icon: Icon, options, selected, onToggle, idKey, testid }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500"><Icon size={13} /> {label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const id = o[idKey]; const on = selected.includes(id);
          return (
            <button key={id} type="button" onClick={() => onToggle(id)} data-testid={`${testid}-${id}`}
              className={`flex items-center gap-1 border px-2.5 py-1 text-xs ${on ? "border-primary bg-primary text-white" : "border-zinc-300 bg-white text-zinc-600 hover:border-zinc-400"}`}>
              {on && <Check size={12} />} {o.displayName}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function RoleTemplates() {
  const { can } = useAuth();
  const isManager = can("roleTemplates.manage");
  const { t } = useLang();
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [teams, setTeams] = useState([]);
  const [sharedMbx, setSharedMbx] = useState([]);
  const [locations, setLocations] = useState([]);
  const [defaultLoc, setDefaultLoc] = useState("US");
  const [companies, setCompanies] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => Promise.all([api.get("/role-templates"), api.get("/groups"), api.get("/licenses"), api.get("/teams")])
    .then(([r, g, l, t]) => { setTemplates(r.data); setGroups(g.data); setLicenses(l.data); setTeams(t.data); });
  useEffect(() => {
    load();
    api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/usage-locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/settings").then((r) => setDefaultLoc(r.data.defaultUsageLocation || "US")).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/users").then((r) => setOrgUsers(r.data)).catch(() => {});
    api.get("/users/shared").then((r) => setSharedMbx(r.data.filter((x) => x.isVisible !== false))).catch(() => {});
  }, []);

  const selCompany = companies.find((c) => c.name === form.companyName);

  const secGroups = groups.filter((g) => !["cloud_distribution", "cloud_teams", "licensing"].includes(g.classification) && g.isVisible !== false && !g.dynamicMembership && !g.applicationId);
  const distGroups = groups.filter((g) => g.classification === "cloud_distribution" && g.isVisible !== false && !g.dynamicMembership && !g.applicationId);
  const licenseGroups = groups.filter((g) => g.classification === "licensing" && g.isVisible !== false && !g.dynamicMembership && !g.applicationId);
  const visTeams = teams.filter((t) => t.isVisible !== false);
  const appOpts = apps.map((a) => ({ ...a, displayName: a.name }));
  const name = (list, id, key = "id") => list.find((x) => x[key] === id)?.displayName || id;

  const startNew = () => { setEditing(null); setForm({ ...EMPTY, usageLocation: defaultLoc }); setOpen(true); };
  const startEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t }); setOpen(true); };
  const toggle = (cat, id) => setForm((p) => ({ ...p, [cat]: p[cat].includes(id) ? p[cat].filter((x) => x !== id) : [...p[cat], id] }));
  const toggleShared = (box) => setForm((p) => {
    const list = p.sharedMailboxes || [];
    const exists = list.find((x) => x.id === box.id);
    return { ...p, sharedMailboxes: exists ? list.filter((x) => x.id !== box.id)
      : [...list, { id: box.id, mail: box.mail || box.userPrincipalName, access: "fullAccess" }] };
  });
  const setSharedAccess = (id, access) => setForm((p) => ({ ...p, sharedMailboxes: (p.sharedMailboxes || []).map((x) => x.id === id ? { ...x, access } : x) }));

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (editing) await api.put(`/role-templates/${editing.id}`, form);
      else await api.post("/role-templates", form);
      toast.success("Template saved"); setOpen(false); await load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/role-templates/${id}`); toast.success("Template deleted"); await load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <>
      <PageHeader title={t("roles.title")} subtitle={t("roles.subtitle")}>
        {isManager && (
          <Button onClick={startNew} data-testid="new-template" className="rounded-sm bg-primary hover:bg-[#002480]"><Plus size={16} className="mr-2" /> New template</Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 gap-px bg-zinc-200 p-6 md:p-10 lg:grid-cols-2">
        {templates.map((t) => (
          <div key={t.id} className="bg-white p-6" data-testid={`template-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center bg-primary/10 text-primary"><LayoutTemplate size={18} /></div>
                <div>
                  <div className="text-sm font-bold text-zinc-900">{t.name}</div>
                  <div className="text-xs text-zinc-500">{t.department} · {t.defaultJobTitle}</div>
                </div>
              </div>
              {isManager && (
                <div className="flex gap-1">
                  <button onClick={() => startEdit(t)} data-testid={`edit-template-${t.id}`} className="p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-primary"><Pencil size={15} /></button>
                  <button onClick={() => del(t.id)} data-testid={`delete-template-${t.id}`} className="p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              )}
            </div>
            <p className="mt-3 text-sm text-zinc-500">{t.description}</p>
            <div className="mt-4 space-y-2 border-t border-zinc-100 pt-3 text-xs">
              {[["Groups", [...t.groups], groups, "id"], ["Apps", [...(t.applications || [])], appOpts, "id"], ["Lists", [...t.distributionLists], groups, "id"], ["Licenses", [...t.licenses], licenses, "skuId"], ["Teams", [...t.teams], teams, "id"]].map(([lbl, ids, list, key]) => (
                <div key={lbl} className="flex gap-2">
                  <span className="w-16 shrink-0 font-bold uppercase tracking-wider text-zinc-400">{lbl}</span>
                  <span className="text-zinc-600">{ids.length ? ids.map((id) => name(list, id, key)).join(", ") : "—"}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto rounded-sm sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">{editing ? "Edit" : "New"} role template</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name" className="rounded-sm border-zinc-300" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Company</Label>
                <datalist id="tpl-company-list">{companies.map((c) => <option key={c.id} value={c.name} />)}</datalist>
                <Input list="tpl-company-list" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} placeholder="Select or type…" data-testid="template-company" className="rounded-sm border-zinc-300" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Department</Label>
                <datalist id="tpl-dept-list">{(selCompany?.departments || []).map((d) => <option key={d.name} value={d.name} />)}</datalist>
                <Input list="tpl-dept-list" value={form.department} onChange={(e) => { const d = selCompany?.departments.find((x) => x.name === e.target.value); setForm({ ...form, department: e.target.value, ...(d ? { defaultManager: d.managerId || "", defaultManagerName: d.managerName || "" } : {}) }); }} placeholder="Select or type…" data-testid="template-dept" className="rounded-sm border-zinc-300" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Default job title</Label>
                <datalist id="tpl-title-list">{(selCompany?.jobTitles || []).map((j) => <option key={j} value={j} />)}</datalist>
                <Input list="tpl-title-list" value={form.defaultJobTitle} onChange={(e) => setForm({ ...form, defaultJobTitle: e.target.value })} placeholder="Select or type…" data-testid="template-title" className="rounded-sm border-zinc-300" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Manager</Label>
                <Select value={form.defaultManager || "__none__"} onValueChange={(v) => { if (v === "__none__") { setForm({ ...form, defaultManager: "", defaultManagerName: "" }); return; } const u = orgUsers.find((x) => x.id === v); setForm({ ...form, defaultManager: v, defaultManagerName: u?.displayName || "" }); }}>
                  <SelectTrigger className="rounded-sm border-zinc-300" data-testid="template-manager"><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(no manager)</SelectItem>
                    {[...orgUsers].sort(byLastName).map((u) => <SelectItem key={u.id} value={u.id}>{u.displayName}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("roles.usageLocation")}</Label>
                <Select value={form.usageLocation || defaultLoc} onValueChange={(v) => setForm({ ...form, usageLocation: v })}>
                  <SelectTrigger className="rounded-sm border-zinc-300" data-testid="template-loc"><SelectValue /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.code} value={l.code}>{l.code} — {l.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="template-desc" className="rounded-sm border-zinc-300" /></div>
            {appOpts.length > 0 && (
              <Chips label={t("roles.applications")} icon={AppWindow} options={appOpts} selected={form.applications} onToggle={(id) => toggle("applications", id)} idKey="id" testid="tpl-app" />
            )}
            <Chips label={t("roles.securityGroups")} icon={Shield} options={secGroups} selected={form.groups} onToggle={(id) => toggle("groups", id)} idKey="id" testid="tpl-group" />
            <Chips label={t("roles.distributionLists")} icon={Mail} options={distGroups} selected={form.distributionLists} onToggle={(id) => toggle("distributionLists", id)} idKey="id" testid="tpl-dl" />
            {licenseGroups.length > 0 ? (
              <div>
                <Chips label={t("roles.licenseGroups")} icon={KeySquare} options={licenseGroups} selected={form.groups} onToggle={(id) => toggle("groups", id)} idKey="id" testid="tpl-licgroup" />
                <p className="mt-1 text-[11px] text-zinc-400">{t("roles.licenseGroupHint")}</p>
              </div>
            ) : (
              <Chips label={t("roles.licensesDirect")} icon={KeySquare} options={licenses} selected={form.licenses} onToggle={(id) => toggle("licenses", id)} idKey="skuId" testid="tpl-lic" />
            )}
            <Chips label={t("roles.teams")} icon={MessagesSquare} options={visTeams} selected={form.teams} onToggle={(id) => toggle("teams", id)} idKey="id" testid="tpl-team" />
            <div>
              <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500"><Inbox size={13} /> {t("roles.sharedMailboxes")}</div>
              {sharedMbx.length === 0 ? <span className="text-xs text-zinc-400">{t("onb.noneAvailable")}</span> : (
                <div className="space-y-2">
                  {sharedMbx.map((box) => {
                    const cur = (form.sharedMailboxes || []).find((x) => x.id === box.id);
                    return (
                      <div key={box.id} className={`flex flex-wrap items-center gap-2 border px-2.5 py-1.5 ${cur ? "border-primary bg-primary/5" : "border-zinc-200"}`}>
                        <button type="button" onClick={() => toggleShared(box)} data-testid={`tpl-shared-${box.id}`}
                          className={`flex items-center gap-1 text-xs ${cur ? "font-semibold text-primary" : "text-zinc-600"}`}>
                          {cur && <Check size={12} />} {box.displayName} <span className="font-mono text-[11px] text-zinc-400">{box.mail || box.userPrincipalName}</span>
                        </button>
                        {cur && (
                          <Select value={cur.access} onValueChange={(v) => setSharedAccess(box.id, v)}>
                            <SelectTrigger className="ml-auto h-7 w-44 rounded-sm border-zinc-300 text-xs" data-testid={`tpl-shared-access-${box.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fullAccess">{t("roles.fullAccess")}</SelectItem>
                              <SelectItem value="sendAs">{t("roles.sendAs")}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-template" className="rounded-sm bg-primary hover:bg-[#002480]">Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
